import { ArrowUpRight, TriangleAlert } from "lucide-react";
import { SITE, IMAGES } from "@/constants/site";
import { Reveal } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const SERVICES = [
  {
    n: "S.01",
    title: "Full-Property Cleanouts",
    desc: "Homes, garages, basements, attics — emptied end to end, broom-swept when we leave.",
    img: IMAGES.junkPile,
    span: "md:col-span-8",
  },
  {
    n: "S.02",
    title: "Furniture & Appliances",
    desc: "Couches, mattresses, washers, dryers, TVs. Gone the same day you call.",
    img: IMAGES.worker,
    span: "md:col-span-4",
  },
  { n: "S.03", title: "Yard Waste & Debris", desc: "Storm cleanup, branches, fencing, renovation debris.", span: "md:col-span-4" },
  { n: "S.04", title: "Office & Commercial", desc: "Office cleanouts, retail spaces, property managers, realtors.", span: "md:col-span-4" },
  { n: "S.05", title: "Light Demolition", desc: "Sheds, decks, hot tubs — torn down, loaded up, hauled away.", span: "md:col-span-4" },
  {
    n: "S.06",
    title: "Estate Cleanouts",
    desc: "Handled with care and respect during difficult transitions. We donate and recycle everything we can.",
    img: IMAGES.cleanRoom,
    span: "md:col-span-12",
  },
];

export const Services = () => (
  <section id="services" data-testid="services-section" className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
    <Reveal>
      <p className="mb-4 text-xs font-bold uppercase tracking-[0.35em] text-acid">What we haul</p>
      <h2 className="mb-16 max-w-3xl font-display text-4xl font-black italic uppercase leading-none tracking-tighter sm:text-5xl lg:text-[5vw]">
        If it’s junk, <span className="text-acid">it’s gone.</span>
      </h2>
    </Reveal>

    <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
      {SERVICES.map((s, i) => (
        <Reveal key={s.n} delay={i * 0.06} className={s.span}>
          <button
            data-testid={`service-card-${s.n.replace(".", "-").toLowerCase()}`}
            onClick={() => scrollToId("quote")}
            className="group relative flex h-full min-h-[240px] w-full flex-col justify-end overflow-hidden border border-seam bg-coal p-8 text-left transition-colors duration-300 hover:border-acid md:min-h-[280px]"
          >
            {s.img && (
              <div className="absolute inset-0">
                <img
                  src={s.img}
                  alt=""
                  className="h-full w-full object-cover opacity-25 grayscale transition-all duration-500 group-hover:scale-105 group-hover:opacity-40"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-transparent" />
              </div>
            )}
            <div className="relative">
              <span className="mb-3 block text-xs font-bold tracking-[0.3em] text-acid">{s.n}</span>
              <h3 className="font-display text-2xl font-extrabold italic uppercase tracking-tight sm:text-3xl">
                {s.title}
              </h3>
              <p className="mt-2 max-w-sm text-sm leading-relaxed text-zinc-400">{s.desc}</p>
              <span className="mt-4 flex items-center gap-1 text-xs font-bold uppercase tracking-[0.25em] text-zinc-500 transition-colors duration-200 group-hover:text-acid">
                Get it hauled <ArrowUpRight className="h-3.5 w-3.5" />
              </span>
            </div>
          </button>
        </Reveal>
      ))}
    </div>

    <Reveal delay={0.1}>
      <div
        data-testid="hazmat-notice"
        className="mt-10 flex flex-col gap-5 border border-seam bg-ink p-8 sm:flex-row sm:items-start md:p-10"
      >
        <span className="flex h-14 w-14 shrink-0 items-center justify-center border border-acid">
          <TriangleAlert className="h-6 w-6 text-acid" />
        </span>
        <div>
          <h3 className="font-display text-2xl font-extrabold italic uppercase tracking-tight">
            What we <span className="text-acid">can’t</span> take
          </h3>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-zinc-400">
            We’re not licensed for hazardous materials — that means no chemicals, paint, solvents, asbestos,
            propane tanks, and nothing containing Freon (fridges, freezers, AC units, dehumidifiers).
            Not sure about an item? Call{" "}
            <a data-testid="hazmat-call-link" href={`tel:${SITE.phoneTel}`} className="font-bold text-acid hover:underline">
              {SITE.phoneDisplay}
            </a>{" "}
            and we’ll point you the right way.
          </p>
        </div>
      </div>
    </Reveal>
  </section>
);
